import React from "react";

import {
    TableRowContext,
    TableRowContextProvider,
    TableRowContextType,
} from "commons/context/tableRow";
import { TableRowsContext } from "commons/context/tableRows";

import { DragAndDropListHook } from "commons/hooks/useDragAndDropList";

import {
    Column,
    DisableOnRowClickInterface,
    EmphasizeRowInterface,
    OnRowClickInterface,
} from "commons/types/table";

import TableRow from "commons/ui/datatable/TableRow";

export type TableRowWrapperPartialParamsType<T> = {
    readonly columns: Array<Column<T>>;
    readonly onRowClick?: OnRowClickInterface<T>;
    readonly disableOnRowClick?: DisableOnRowClickInterface<T>;
    readonly emphasizeRow?: EmphasizeRowInterface<T>;
    readonly hideTableHead?: boolean;
    readonly dragAndDropHook?: DragAndDropListHook<HTMLTableRowElement>;
    readonly loading?: boolean;
};

export function calculateRows<T>({
    data,
    columns,
    onRowClick,
    disableOnRowClick,
    emphasizeRow,
    renderRowChild,
    hideTableHead,
    dragAndDropHook,
}: TableRowWrapperPartialParamsType<T> & {
    readonly data: Array<T>;
    readonly renderRowChild?: (row: T) => React.ReactNode;
}): Array<JSX.Element> {
    return data.map((row, j: number) => (
        <TableRowWrapper
            key={j}
            index={j}
            row={row}
            columns={columns}
            onRowClick={onRowClick}
            disableOnRowClick={disableOnRowClick}
            emphasizeRow={emphasizeRow}
            renderRowChild={renderRowChild}
            hideTableHead={hideTableHead}
            dragAndDropHook={dragAndDropHook}
        />
    ));
}

type TableRowWrapperPropsType<T> = TableRowWrapperPartialParamsType<T> & {
    readonly row: T;
    readonly index: number;
};

function TableRowWrapper<T>({
    row,
    index,
    columns,
    hideTableHead,
    onRowClick,
    disableOnRowClick,
    emphasizeRow,
    renderRowChild,
    dragAndDropHook,
}: TableRowWrapperPropsType<T> & {
    readonly renderRowChild?: (row: T) => React.ReactNode;
}) {
    const tableRowsContextValue = React.useMemo(() => ({ index }), [index]);

    return (
        <TableRowsContext.Provider value={tableRowsContextValue}>
            <TableRowContextProvider>
                <TableRow
                    row={row}
                    columns={columns}
                    index={index}
                    onRowClick={onRowClick}
                    disableOnRowClick={disableOnRowClick}
                    emphasizeRow={emphasizeRow}
                    hideTableHead={hideTableHead}
                    dragAndDropHook={dragAndDropHook}
                />

                <TableRowContext.Consumer>
                    {(value: TableRowContextType) => {
                        if (value.showChild && renderRowChild !== undefined) {
                            return renderRowChild(row);
                        }
                    }}
                </TableRowContext.Consumer>
            </TableRowContextProvider>
        </TableRowsContext.Provider>
    );
}
